import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TextField, Box, useTheme } from '@mui/material';
import { StyledTextField, timeInputSx, timeInputSeparatorSx, timeSelectStyle } from './InputStyles';

dayjs.locale('ru');

const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm:ss';
const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Стили для DatePicker/DateTimePicker
 * Typography standards:
 * - Input value: subtitle1 (24px) + fontWeight 500
 * - Label: subtitle1 (24px) + fontWeight 600
 */
const getInputSx = (theme) => ({
    '& .MuiFormLabel-root': {
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 600,
    },
    '& .MuiInputBase-input': {
        padding: '1rem 1.25rem',
        fontSize: theme.typography.subtitle1.fontSize,
        fontWeight: 500,
    },
    '& .MuiInputLabel-outlined': {
        transform: 'translate(14px, 1rem) scale(1)',
        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.85)',
            fontWeight: 600,
        },
    },
});

/**
 * DateInput - компонент для выбора даты
 */
export function DateInput({ label, value, onChange, disabled = false, ...rest }) {
    const theme = useTheme();

    const handleChange = (newValue) => {
        if (onChange && newValue) {
            onChange(dayjs(newValue).format(DATE_FORMAT));
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <DatePicker
                label={label}
                value={value ? dayjs(value) : null}
                onChange={handleChange}
                disabled={disabled}
                inputFormat="DD.MM.YYYY"
                renderInput={(params) => <TextField {...params} fullWidth variant="outlined" sx={getInputSx(theme)} />}
                {...rest}
            />
        </LocalizationProvider>
    );
}

// Опции для селектов
const hoursOptions = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minutesOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
const secondsOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

/**
 * TimeSelect - нативный select с явным размером шрифта
 */
function TimeSelect({ value, options, onChange, disabled, fontSize }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            style={{
                ...timeSelectStyle,
                fontSize: fontSize,
            }}
        >
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    );
}

/**
 * TimeInputComponent - внутренний компонент с forwardRef для MUI TextField
 */
const TimeInputComponent = React.forwardRef(function TimeInputComponent(props, ref) {
    const { hours, minutes, seconds, onPartChange, disabled, fontSize } = props;

    return (
        <Box ref={ref} sx={{...timeInputSx, fontSize: fontSize}}>
            <TimeSelect
                value={hours}
                options={hoursOptions}
                onChange={(val) => onPartChange('hours', val)}
                disabled={disabled}
                fontSize={fontSize}
            />
            <Box component="span" sx={{...timeInputSeparatorSx, fontSize: fontSize}}>:</Box>
            <TimeSelect
                value={minutes}
                options={minutesOptions}
                onChange={(val) => onPartChange('minutes', val)}
                disabled={disabled}
                fontSize={fontSize}
            />
            <Box component="span" sx={{...timeInputSeparatorSx, fontSize: fontSize}}>:</Box>
            <TimeSelect
                value={seconds}
                options={secondsOptions}
                onChange={(val) => onPartChange('seconds', val)}
                disabled={disabled}
                fontSize={fontSize}
            />
        </Box>
    );
});

/**
 * TimeInput - выбор времени с тремя стилизованными селектами
 * Использует StyledTextField для единообразия с другими инпутами
 */
export function TimeInput({ label, value, onChange, disabled = false }) {
    const theme = useTheme();
    const timeValue = value || '00:10:00';
    const [hours, minutes, seconds] = timeValue.split(':');
    const fontSize = theme.typography.subtitle1.fontSize;

    const handleChange = (part, val) => {
        const parts = { hours, minutes, seconds };
        parts[part] = val;
        if (onChange) {
            onChange(`${parts.hours}:${parts.minutes}:${parts.seconds}`);
        }
    };

    return (
        <StyledTextField
            label={label}
            variant="outlined"
            fullWidth
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
            InputProps={{
                inputComponent: TimeInputComponent,
                inputProps: {
                    hours,
                    minutes,
                    seconds,
                    onPartChange: handleChange,
                    disabled,
                    fontSize,
                },
            }}
        />
    );
}

/**
 * DateTimeInput - компонент для выбора даты и времени
 */
export function DateTimeInput({ label, value, onChange, disabled = false, ...rest }) {
    const theme = useTheme();

    const handleChange = (newValue) => {
        if (onChange && newValue) {
            onChange(dayjs(newValue).format(DATETIME_FORMAT));
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <DateTimePicker
                label={label}
                value={value ? dayjs(value) : null}
                onChange={handleChange}
                disabled={disabled}
                inputFormat="DD.MM.YYYY HH:mm"
                ampm={false}
                renderInput={(params) => <TextField {...params} fullWidth variant="outlined" sx={getInputSx(theme)} />}
                {...rest}
            />
        </LocalizationProvider>
    );
}

export default DateInput;
